import { ReactNode, useCallback, useEffect, useId, useRef } from 'react';

/**
 * UiModal - Reusable modal component using Tailwind CSS
 *
 * Features:
 * - Backdrop overlay with configurable click-to-close
 * - Centered modal dialog with responsive sizing
 * - Header with title and optional close button
 * - Scrollable body content
 * - Footer for actions
 * - Keyboard support (ESC to close)
 * - No Bootstrap JavaScript dependency
 */

export interface UiModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | 'screen';
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  titleClassName?: string;
  backdropClassName?: string;
  bodyClassName?: string;
  /**
   * Optional content rendered inside the header bar between the title and the
   * close button. Mirrors AngularJS modalDialog.html `.loan-info` / `.popout-btn`
   * slots — keeps title and right-side details on a single row.
   */
  headerRight?: ReactNode;
}

export const UiModal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = '',
  titleClassName = '',
  bodyClassName = '',
  headerRight,
}: UiModalProps) => {
  const titleId = useId();
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  // Save the element that triggered the modal for focus restoration
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
    }
  }, [isOpen]);

  // Focus trap: keep Tab cycling within the modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [closeOnEscape, onClose]
  );

  // Attach keydown listener (re-attaches when handler changes)
  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  // Auto-focus the modal dialog on open (runs only once per open, NOT on handler changes)
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      modalRef.current?.focus();
    });
  }, [isOpen]);

  // Restore focus to trigger element on close
  useEffect(() => {
    if (!isOpen && triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-[300px]',
    md: 'max-w-[600px]',
    lg: 'max-w-[900px]',
    xl: 'max-w-[1140px]',
    '2xl': 'max-w-[1200px]',
    '4xl': 'max-w-[60vw]',
    screen: 'max-w-[80vw]',
  };

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className='fixed inset-0 bg-black bg-opacity-40 z-[99999990]'
        onClick={closeOnBackdrop ? onClose : undefined}
      />

      <div
        className='fixed inset-0 z-[99999999] overflow-x-hidden overflow-y-auto px-4'
        onClick={closeOnBackdrop ? onClose : undefined}
      >
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`bg-white rounded-lg shadow-xl ${sizeClasses[size]} w-full mx-auto mt-[2rem] mb-[30px] focus:outline-none ${className}`}
          onClick={(e) => e.stopPropagation()}
          role='dialog'
          aria-modal='true'
          aria-labelledby={titleId}
        >
          {/* Header — single row: title (left) + headerRight slot + close (right)
              Mirrors AngularJS modalDialog.html .modal-header-top { display:flex; justify-content:space-between } */}
          <div className='flex items-center justify-between px-4 py-3 border-b bg-angular-blue gap-x-3'>
            <h4
              id={titleId}
              className={`text-[18px] font-medium text-white uppercase shrink-0 ${titleClassName}`}
            >
              {title}
            </h4>
            {headerRight && (
              <div className='flex items-center justify-end flex-1 min-w-0'>{headerRight}</div>
            )}
            {showCloseButton && (
              <a
                href='#'
                role='button'
                className='text-white opacity-80 hover:opacity-100 focus:outline-none transition-opacity shrink-0'
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                }}
                aria-label='Close'
              >
                <span className='text-[36px] font-thin leading-none' aria-hidden='true'>
                  &times;
                </span>
              </a>
            )}
          </div>

          {/* Body — matches Bootstrap 3 .modal-body { padding: 15px } */}
          <div className={`p-[15px] ${bodyClassName}`}>{children}</div>

          {/* Footer */}
          {footer && <div className='flex justify-center gap-3 px-4 py-3 border-t'>{footer}</div>}
        </div>
      </div>
    </>
  );
};

/**
 * UiModalBody - Optional wrapper for modal body content with custom styling
 */
interface UiModalBodyProps {
  children: ReactNode;
  className?: string;
}

export const UiModalBody = ({ children, className = '' }: UiModalBodyProps) => {
  return <div className={className}>{children}</div>;
};

/**
 * UiModalFooter - Helper for consistent footer styling
 */
interface UiModalFooterProps {
  children: ReactNode;
  align?: 'left' | 'center' | 'right';
}

export const UiModalFooter = ({ children, align = 'center' }: UiModalFooterProps) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  return <div className={`flex gap-3 ${alignClasses[align]}`}>{children}</div>;
};
